<template>
  <div class="payout-options-wrapper">
    <!-- Loading state -->
    <div v-if="loading" class="text-center py-4">
      <i class="fa fa-spinner fa-spin"></i>
      Loading payout options...
    </div>

    <!-- Payout options list -->
    <div v-else>
      <div
        v-for="option in payoutOptionTypes"
        :key="option.id"
        class="payout-item"
        :class="{ 'is-enabled': getOptionEnabled(option.id) }"
      >
        <label class="payout-toggle">
          <input
            type="checkbox"
            :checked="getOptionEnabled(option.id)"
            @change="toggleOption(option.id, $event.target.checked)"
          />
          <span class="checkmark"></span>
          <span class="payout-name">{{ option.option_name }}</span>
        </label>

        <div class="payout-fields" v-if="getOptionEnabled(option.id)">
          <div class="field-group">
            <label>Discount:</label>
            <input
              type="number"
              :value="getOptionValue(option.id, 'discount_value')"
              @input="
                setOptionValue(option.id, 'discount_value', $event.target.value)
              "
            />
            <select
              :value="getOptionValue(option.id, 'discount_type')"
              @change="
                setOptionValue(option.id, 'discount_type', $event.target.value)
              "
            >
              <option value="percent">Percent</option>
              <option value="flat">Flat</option>
            </select>
          </div>
          <div class="field-group">
            <label>Max Transfer Limit:</label>
            <input
              type="number"
              :value="getOptionValue(option.id, 'max_transfer_limit')"
              @input="
                setOptionValue(
                  option.id,
                  'max_transfer_limit',
                  $event.target.value,
                )
              "
            />
          </div>
          <div class="field-group">
            <label>Auto Pay Limit:</label>
            <input
              type="number"
              :value="getOptionValue(option.id, 'autopay_limit')"
              @input="
                setOptionValue(option.id, 'autopay_limit', $event.target.value)
              "
            />
          </div>
        </div>
      </div>

      <!-- No options available -->
      <div v-if="payoutOptionTypes.length === 0" class="alert alert-warning">
        No payout option types available.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PayoutOptions",
  props: {
    countryId: String,
  },
  data() {
    return {
      loading: true,
      payoutOptionTypes: [], // Master list from /payout-option-types/list
      countryPayoutOptions: {}, // Country-specific options keyed by payout_option_id
    };
  },
  mounted() {
    this.initializeData();
  },
  watch: {
    countryId(newVal) {
      if (newVal) {
        this.initializeData();
      }
    },
  },
  methods: {
    async initializeData() {
      this.loading = true;
      try {
        // First fetch master list of payout option types
        await this.fetchPayoutOptionTypes();
        // Then fetch country-specific options
        await this.fetchCountryPayoutOptions();
      } catch (error) {
        console.error("Error initializing data:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchPayoutOptionTypes() {
      try {
        const token = localStorage.getItem("jwtToken");
        const response = await this.$http.get(
          `${this.$baseurl}country/payout-option-types/list`,
          {
            headers: { Authorization: "Bearer " + token },
          },
        );

        if (response.data.details) {
          this.payoutOptionTypes = response.data.details;
        }
      } catch (error) {
        console.error("Error fetching payout option types:", error);
      }
    },

    async fetchCountryPayoutOptions() {
      if (!this.countryId) return;

      try {
        const token = localStorage.getItem("jwtToken");
        const response = await this.$http.get(
          `${this.$baseurl}country/${this.countryId}/payout-options`,
          {
            headers: { Authorization: "Bearer " + token },
          },
        );

        if (response.data.code === "0003-000" && response.data.details) {
          // Map response to countryPayoutOptions keyed by payout_option_id
          const options = {};
          response.data.details.forEach((item) => {
            const optionId = item.payout_option_id || item.payout_option?.id;
            if (optionId) {
              options[optionId] = {
                id: item.id, // The country_payout_option record ID
                payout_option_id: optionId,
                is_enabled: item.is_enabled === 1 || item.is_enabled === true,
                discount_value: parseFloat(item.discount_value) || "",
                discount_type: item.discount_type || "percent",
                max_transfer_limit: parseFloat(item.max_transfer_limit) || "",
                autopay_limit: parseFloat(item.autopay_limit) || "",
              };
            }
          });
          this.countryPayoutOptions = options;
        }
      } catch (error) {
        console.error("Error fetching country payout options:", error);
      }
    },

    getOptionEnabled(optionId) {
      return this.countryPayoutOptions[optionId]?.is_enabled || false;
    },

    getOptionValue(optionId, field) {
      return this.countryPayoutOptions[optionId]?.[field] || "";
    },

    toggleOption(optionId, enabled) {
      if (!this.countryPayoutOptions[optionId]) {
        // Initialize new option
        this.$set(this.countryPayoutOptions, optionId, {
          payout_option_id: optionId,
          is_enabled: enabled,
          discount_value: "",
          discount_type: "percent",
          max_transfer_limit: "",
          autopay_limit: "",
        });
      } else {
        this.$set(this.countryPayoutOptions[optionId], "is_enabled", enabled);
      }
    },

    setOptionValue(optionId, field, value) {
      if (!this.countryPayoutOptions[optionId]) {
        this.$set(this.countryPayoutOptions, optionId, {
          payout_option_id: optionId,
          is_enabled: true,
          discount_value: "",
          discount_type: "percent",
          max_transfer_limit: "",
          autopay_limit: "",
        });
      }
      this.$set(this.countryPayoutOptions[optionId], field, value);
    },

    async savePayoutOptions() {
      if (!this.countryId) {
        this.$emit("save-error", "No country selected");
        return false;
      }

      try {
        const token = localStorage.getItem("jwtToken");

        // Build payout_options array using payout_option_id (UUID)
        const payoutOptionsArray = this.payoutOptionTypes.map((optionType) => {
          const optionData = this.countryPayoutOptions[optionType.id] || {};
          return {
            payout_option_id: optionType.id,
            is_enabled: optionData.is_enabled || false,
            discount_value: parseFloat(optionData.discount_value) || 0,
            discount_type: optionData.discount_type || "percent",
            max_transfer_limit: parseFloat(optionData.max_transfer_limit) || 0,
            autopay_limit: parseFloat(optionData.autopay_limit) || 0,
          };
        });

        const response = await this.$http.put(
          `${this.$baseurl}country/${this.countryId}/payout-options`,
          { payout_options: payoutOptionsArray },
          { headers: { Authorization: "Bearer " + token } },
        );

        if (response.data.code === "0003-000") {
          this.$emit("save-success", response.data.message);
          return true;
        } else {
          this.$emit("save-error", response.data.message);
          return false;
        }
      } catch (error) {
        console.error("Error saving payout options:", error);
        this.$emit(
          "save-error",
          error.response?.data?.message || "Error saving payout options",
        );
        return false;
      }
    },
  },
};
</script>

<style scoped>
.payout-options-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.payout-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  transition: all 0.2s ease;
  margin-bottom: 16px;
}

.payout-item:last-child {
  margin-bottom: 0;
}

.payout-item.is-enabled {
  border-color: #4caf50;
  background: #fff;
}

.payout-toggle {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;
  margin: 0;
  user-select: none;
}

.payout-toggle input[type="checkbox"] {
  display: none;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid #bbb;
  border-radius: 4px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.payout-toggle input:checked + .checkmark {
  background: #4caf50;
  border-color: #4caf50;
}

.payout-toggle input:checked + .checkmark::after {
  content: "✓";
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.payout-name {
  font-weight: 500;
  font-size: 15px;
  color: #333;
}

.payout-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 16px 16px 48px;
  border-top: 1px solid #eee;
  margin-top: 0;
  padding-top: 16px;
}

.field-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-group label {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  margin: 0;
}

.field-group input[type="number"] {
  width: 100px;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.field-group input[type="number"]:focus {
  outline: none;
  border-color: #4caf50;
}

.field-group select {
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.field-group select:focus {
  outline: none;
  border-color: #4caf50;
}
</style>
